import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

interface GateCheckResponse {
  passed: boolean;
  gaps: Array<{
    fieldName: string;
    displayName: string;
    requirementType: string;
    isDeferrable: boolean;
  }>;
}

interface Props {
  opportunityId: string;
  targetStageId: string;
}

/**
 * Story 21.5 — compact gate status indicator for pipeline board cards.
 * Green = all met, yellow = optional gaps, red = blocked.
 */
export function GateStatusBadge({ opportunityId, targetStageId }: Props) {
  const [status, setStatus] = useState<'loading' | 'pass' | 'warn' | 'block'>('loading');
  const [tooltip, setTooltip] = useState('');

  useEffect(() => {
    apiClient
      .get<GateCheckResponse>(`/gate-check/${opportunityId}/${targetStageId}`)
      .then(({ data }) => {
        if (data.gaps.length === 0) {
          setStatus('pass');
          setTooltip('All requirements met');
        } else if (data.passed) {
          setStatus('warn');
          const names = data.gaps.map((g) => g.displayName).join(', ');
          setTooltip(`Optional: ${names}`);
        } else {
          setStatus('block');
          const names = data.gaps
            .filter((g) => g.requirementType === 'MANDATORY')
            .map((g) => g.displayName)
            .join(', ');
          setTooltip(`Blocked: ${names}`);
        }
      })
      .catch(() => setStatus('pass'));
  }, [opportunityId, targetStageId]);

  if (status === 'loading') return null;

  const colors = {
    pass: 'bg-emerald-500',
    warn: 'bg-amber-500',
    block: 'bg-red-500',
  };

  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${colors[status]}`}
      title={tooltip}
    />
  );
}
